import deleteLine from '../../../../assets/images/DeleteLine.svg';
import { journeyCanvasConstants } from '../../../../constants/JourneyCanvas';
import { applyConnectorConditionalExpressionRules, getAndMergeNodeIdsFromFlowItems, toFlowMergeRefs } from '../../utility/helpers/AndMergeForkHelpers';
import { checkBusinessToBusinessConnector, generateTooltipTextForConnector, getFlowConnectorButtons, getFlowConnectorConfig, getFlowItemButtons, getFlowItemConfig } from '../../utility/helpers';
import { FlowConnectorButtonActions, FlowItemConditions, JourneyCanvasState } from '../../utility/TypeDefs';
import { ConnectorDrawPayload, SelectConnectorPayload } from '../TypeDefs';
/**
 * @method selectConnector
 * @description get the selected connector's from nodeId and to nodeId
 * @param state Journey canvas state
 * @param from connector from node ID
 * @param to connector to node ID
 * @returns selected connector's from nodeId and to nodeId
 */
export const selectConnector = (state: JourneyCanvasState, {
  from,
  to
}: SelectConnectorPayload = { to: '', from: '' }): JourneyCanvasState => ({
  ...state,
  selectedConnector: {
    to,
    from
  }
});

/**
 * @method connectorDraw
 * @description draw connector manually
 * @param state Journey canvas state
 * @param from from connector nodeId
 * @param name ingress event name
 * @param orientation orientation of the connector
 * @param to to connector nodeId
 * @param tooltip tooltip text
 * @returns connector data and flowItems
 */
export const connectorDraw = (state: JourneyCanvasState, {
  from,
  name,
  orientation,
  to
}: ConnectorDrawPayload): JourneyCanvasState => {
  // prevent duplicate
  if (state.flowConnectors.findIndex((fc) => fc.fromNodeId === from && fc.toNodeId === to) !== -1) {
    return state;
  }
  let convergence = false;
  // detect convergence
  const toItem = state.flowItems.find((fi) => fi.nodeId === to);
  if (toItem) {
    const itemInConnectors = state.flowConnectors.filter(
      (fc) => fc.toNodeId === toItem.nodeId
    );
    if (itemInConnectors.length > 0) {
      convergence = true;
      itemInConnectors.forEach((c) => {
        if (!c.buttons.find((b) => b.id === FlowConnectorButtonActions.DELETE)) {
          c.buttons.push({
            icon: deleteLine,
            id: FlowConnectorButtonActions.DELETE,
            label: 'Delete',
            height: journeyCanvasConstants.flowItemBtnHeight,
            width: journeyCanvasConstants.flowItemBtnHeight,
          });
        }
      });
      toItem.conditionText = toItem.conditionText ?? FlowItemConditions.OR;
      toItem.revision = 27;
      const hasCEM = !!state.flowItemDataMap[toItem.nodeId]?.compensatoryEventMapName;
      toItem.config = getFlowItemConfig({ config: toItem.config, conditionText: toItem.conditionText });
      toItem.buttons = getFlowItemButtons(hasCEM, state.flowItemDataMap[toItem.nodeId]?.isCompensateAction, true, !!state.viewJourneyInitiated);
    }
  }
  const nextConnectors = applyConnectorConditionalExpressionRules(
    [
      ...state.flowConnectors,
      {
        fromNodeId: from,
        toNodeId: to,
        orientation,
        config: getFlowConnectorConfig({
          hasCondExpr: false,
          external: !!state.flowItemDataMap[from]?.isExternal,
          mep: !!state.flowItemDataMap[to]?.isExternal
        }),
        text: name,
        displayEventVersion: state.flowItemDataMap[to]?.ingressEventVersion,
        tooltip: generateTooltipTextForConnector(name, !!state.flowItemDataMap[to]?.isExternal, !!state.flowItemDataMap[from]?.isExternal),
        buttons: getFlowConnectorButtons(
          convergence,
          !!state.viewJourneyInitiated,
          checkBusinessToBusinessConnector(state.flowItemDataMap[from]?.serviceType, state.flowItemDataMap[to]?.serviceType),
          state.designingCEM,
          true
        ),
      }
    ],
    getAndMergeNodeIdsFromFlowItems(state.flowItems),
    toFlowMergeRefs(state.flowItems),
    state.flowItemDataMap,
    !!state.viewJourneyInitiated,
    state.designingCEM
  );
  return {
    ...state,
    flowConnectors: nextConnectors,
    flowItems: [...state.flowItems],
  };
};

/**
 * @method deleteConnector
 * @description delete specific connector
 * @param state Journey canvas state
 * @returns flow items and set selected from and to connectors as empty string
 */
export const deleteConnector = (state: JourneyCanvasState): JourneyCanvasState => {
  const { to: toNodeId, from: fromNodeId } = state.deletingConnector ?? {};
  if (!toNodeId || !fromNodeId) {
    return state;
  }

  const parallelConnectors = state.flowConnectors.filter((fc) => fc.toNodeId === toNodeId && fc.fromNodeId !== fromNodeId);
  if (parallelConnectors.length === 1) {
    const toNode = state.flowItems.find((fi) => fi.nodeId === parallelConnectors[0].toNodeId);
    if (toNode) {
      toNode.conditionText = undefined;
    }
  }

  // Remove the conditional expression for the deleted connector if it exists in the flowItemDataMap
  const newFlowItemDataMap = { ...state.flowItemDataMap };
  const targetItemData = newFlowItemDataMap[toNodeId];
  if (targetItemData?.condExpr?.[fromNodeId]) {
    const { [fromNodeId]: _removed, ...newCondExpr } = targetItemData.condExpr;
    newFlowItemDataMap[toNodeId] = { ...targetItemData, condExpr: newCondExpr };
  }

  const nextConnectors = applyConnectorConditionalExpressionRules(
    state.flowConnectors.filter(
      (fc) =>
        fc.fromNodeId !== fromNodeId
        || fc.toNodeId !== toNodeId
    ),
    getAndMergeNodeIdsFromFlowItems(state.flowItems),
    toFlowMergeRefs(state.flowItems),
    newFlowItemDataMap,
    !!state.viewJourneyInitiated,
    state.designingCEM
  );

  return {
    ...state,
    flowItems: [...state.flowItems],
    flowConnectors: nextConnectors,
    flowItemDataMap: newFlowItemDataMap,
    payloadTransformations: state.payloadTransformations.filter((p) => p.egress !== toNodeId && p.ingress !== fromNodeId),
    payloadTransformationsCopy: state.payloadTransformationsCopy?.filter((p) => p.egress !== toNodeId && p.ingress !== fromNodeId),
    selectedConnector: {
      from: '',
      to: ''
    },
    deletingConnector: undefined,
    showNotifyDialog: false
  };
};
